import React, { ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { colors } from 'components/styles/variables';
import { applyOpacityToColor } from 'lib/utils';
import drawerModalStyles from 'components/Shared/DrawerModal/styles';

export type Anchor = 'left' | 'right' | 'top' | 'bottom' | 'center';

interface DrawerModalProps {
  open: boolean;
  anchor?: Anchor;
  onClose: () => void;
  children: ReactNode;
  className?: string;
  overlayColor?: string;
  width?: string;
  height?: string;
  zIndex?: number;
}

const anchorClassMap: Record<Anchor, string> = {
  left: 'drawer-anchor-left',
  right: 'drawer-anchor-right',
  top: 'drawer-anchor-top',
  bottom: 'drawer-anchor-bottom',
  center: 'drawer-anchor-center',
};

const initialTransforms: Record<Anchor, string> = {
  left: 'translateX(-100%)',
  right: 'translateX(100%)',
  top: 'translateY(-100%)',
  bottom: 'translateY(100%)',
  center: 'translateY(40px)', // slide up for center
};

const activeTransforms: Record<Anchor, string> = {
  left: 'translateX(0) translateY(0)',
  right: 'translateX(0) translateY(0)',
  top: 'translateX(0) translateY(0)',
  bottom: 'translateX(0) translateY(0)',
  center: 'translateY(0)',
};

function getInitialTransform(anchor: Anchor): string {
  return initialTransforms[anchor] || initialTransforms.right;
}

function getActiveTransform(anchor: Anchor): string {
  return activeTransforms[anchor] || activeTransforms.right;
}

const ANIMATION_DURATION = 350;

type AnimationState = 'closed' | 'opening' | 'open' | 'closing';
const overlayColorDefault = applyOpacityToColor(colors.BLACK, 0.4);

export function DrawerModal({
  open,
  anchor = 'right',
  onClose,
  children,
  className,
  overlayColor = overlayColorDefault,
  width,
  height,
  zIndex = 1100,
}: DrawerModalProps): JSX.Element | null {
  // Prevent background scroll when open
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  const [animationState, setAnimationState] = useState<AnimationState>('closed');
  const [shouldAnimateIn, setShouldAnimateIn] = useState(false);
  const closeTimeout = useRef<NodeJS.Timeout | null>(null);

  // Handle open/close transitions
  useEffect(() => {
    if (open) {
      if (animationState === 'closed') {
        setAnimationState('opening');
        setShouldAnimateIn(false); // reset
      } else if (animationState === 'closing') {
        setAnimationState('opening');
        setShouldAnimateIn(false);
      }
    } else if (animationState === 'open' || animationState === 'opening') {
      setAnimationState('closing');
      setShouldAnimateIn(false);
      closeTimeout.current = setTimeout(() => {
        setAnimationState('closed');
      }, ANIMATION_DURATION);
    }
    return () => {
      if (closeTimeout.current) {
        clearTimeout(closeTimeout.current);
        closeTimeout.current = null;
      }
    };
  }, [animationState, open]);

  // After mount, trigger animate-in for opening
  useLayoutEffect(() => {
    if (animationState === 'opening' && !shouldAnimateIn) {
      requestAnimationFrame(() => setShouldAnimateIn(true));
    }
    if (animationState === 'opening' && shouldAnimateIn) {
      // After animation starts, set to 'open' after duration
      const t = setTimeout(() => setAnimationState('open'), ANIMATION_DURATION);
      return () => clearTimeout(t);
    }
    return () => {};
  }, [animationState, shouldAnimateIn]);

  if (typeof window === 'undefined' || animationState === 'closed') {
    return null;
  }

  const isVisible = animationState === 'open' || (animationState === 'opening' && shouldAnimateIn);

  const overlayStyle = {
    display: isVisible ? 'block' : 'none',
    zIndex,
    background: overlayColor,
  } as React.CSSProperties;

  const drawerStyle = {
    zIndex: zIndex + 1,
    width: width && ['left', 'right', 'center'].includes(anchor) ? width : undefined,
    height: height && ['top', 'bottom', 'center'].includes(anchor) ? height : undefined,
    transform: isVisible ? getActiveTransform(anchor) : getInitialTransform(anchor),
    transition: `transform ${ANIMATION_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1)`,
  } as React.CSSProperties;

  const modalContent = (
    <>
      <div
        className="custom-modal-overlay"
        style={overlayStyle}
        onClick={onClose}
        data-testid="custom-modal-overlay"
      />
      <div
        className={`custom-modal-drawer ${anchorClassMap[anchor]}${
          className ? ` ${className}` : ''
        }`}
        style={drawerStyle}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        data-testid="custom-modal-drawer"
      >
        <div className="custom-drawer-content">{children}</div>
      </div>
      <style jsx>{drawerModalStyles}</style>
    </>
  );

  return ReactDOM.createPortal(modalContent, document.body);
}
